import React, { useState, useRef, useEffect } from "react";
import "./AddDevice.css";
import { toast, ToastContainer } from "react-toastify";
import {uploadFileApi} from "../../../services/api/inputService";
import { PYTHON_API_BASE_URL } from "../../../services/api/apiConstants";
import formConfig from "../../../datafiles/form_add_device.json";
import { useNavigation } from "../../../contexts/NavigationContext";
import { fracToPct } from "../domain/units";
import { csvWarnings, evSocFromCsvStats } from "../domain/csvStats";
import { socErrors } from "../domain/deviceValidation";
import { listCouplingCandidates } from "../domain/deviceSelectors";
import { formValuesToDeviceType } from "../domain/deviceCoupling";
import CoupledDevicesField from "./CoupledDevicesField";

// Form values without what an upload put there: the file name and the file's
// date range, as a new form starts. Every section's upload shares them.
function withoutUpload(values) {
  const { start_date, end_date, ...rest } = values;
  return { ...rest, csv_file_name: "" };
}

// The other form values an upload fills in (see uploadfile): an EV file's SOC,
// a solar file's operating power range and a load file's max power. A new
// Device Type starts them blank, as a new form does; a storage mode sets its
// own SOC defaults when it's chosen.
const UPLOAD_FILLED_VALUES_BLANK = {
  initial_soc: "",
  min_soc: "",
  max_soc: "",
  min_power_charging: "",
  max_power_charging: "",
};

const AddDevicePopup = ({
  activeTab,
  onClose,
  onSave,
  device = null,
  addDevice: addDeviceProp,
  updateDevice: updateDeviceProp,
  sysconfig: sysconfigProp,
  storageAccount: storageAccountProp,
  microgridName: microgridNameProp,
  datasetName: datasetNameProp,
}) => {
  

  // This React state controls a confirmation dialog popup. 
  // - isOpen: whether the dialog is visible
  // - title: dialog title/message
  // - message: the explanation/question in the dialog
  // - onConfirm: callback for when the user confirms/accepts
  // Example: Used for "Are you sure you want to save/delete?" type popups.
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: null,
  });
  
  const { selectedBranchId } = useNavigation();
  const [customer, setCustomer] = useState(null);
  const token = sessionStorage.getItem("accessToken");
  const [customerSimulationName, setCustomerSimulationName] = useState("");
  const [storageAccountName, setStorageAccountName] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [uploadMessage, setUploadMessage] = useState("");
  // Warnings from csv-parse-metadata about an otherwise valid file.
  const [uploadWarnings, setUploadWarnings] = useState([]);
  // True while the EV SOC fields hold values read from an availability file:
  // they're disabled and not validated. An upload sets it (false when the file
  // had no SOC above zero). A new EV waits for its file, so it starts true. A
  // saved EV doesn't record where its SOC came from, so editing one starts
  // false: its SOC is editable and validated like a BESS's until a fresh
  // upload supplies file values.
  const [evSocFromCsv, setEvSocFromCsv] = useState(!device);
  const [uploading, setUploading] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertAction, setAlertAction] = useState(null);
  const contentRef = useRef(null); // scrollable container
  const [showConfirm, setShowConfirm] = useState(false);
  const lastMaxPowerDischargingRef = useRef(null);
  const lastDischargingEfficiencyRef = useRef(null);
  // Ref to store prior values before switching to "forbidden" so we can restore them
  const priorDischargingValuesRef = useRef({
    max_power_discharging: null,
    discharging_efficiency_fraction: null
  });
  const [formData, setFormData] = useState({
    device_type: activeTab === "All" ? "Production" : (activeTab || ""),
    storage_mode: "",
    device_name: "",
    make_and_model: "",
    serial_number: "",
    status: "connected",
    max_energy: "",
    daily_cycles: "",
    initial_soc: "",
    min_soc: "",
    max_soc: "",
    max_power_charging: "",
    min_power_charging: "",
    max_power_discharging: "",
    charging_efficiency_fraction: "",
    discharging_efficiency_fraction: "",
    avg_parasitic_load: "",
    fractional_self_loss: "",
    average_energy_used_during_operation: "",
    yearly_degradation: "0",
    demand_charge: "",
    production_type: "",
    startup_cost: "",
    fuel_storage_size: "",
    carbon_emissions: "",
    consumption: "",
    fuel_cost_per_kg: "",
    fuel_cost_escalator_fraction: "",
    fuel_type: "",
    csv_file_name: "",
    charging_strategy: "",
    discharging_strategy: "",
    uploadedFile: "",
    demand_charge_escalator_fraction: "",
    // device_ids this device is coupled to; saved on both sides of each link.
    coupled_device_ids: [],
  });

  const [errors, setErrors] = useState({});
  const [onAlertConfirm, setOnAlertConfirm] = React.useState(null);
  
  // Single source of truth for discharging forbidden state
  const isDischargeForbidden = formData.discharging_strategy === "forbidden";

  // Live-computed Min SOC floor (%) implied by the current parasitic load
  // and max energy. Returns null when either value is missing or non-positive.
  // Drives the inline helper text under Min SOC and the submit-time
  // validation. The +1 is a 1%-of-capacity safety buffer.
  const minSocFloorPercent = (() => {
    const pl = Number(formData.avg_parasitic_load);
    const me = Number(formData.max_energy);
    if (!Number.isFinite(pl) || !Number.isFinite(me) || pl <= 0 || me <= 0) {
      return null;
    }
    return (pl / me) * 100 + 1;
  })();

  // Reactive flag: true when the current Min SOC value violates the
  // parasitic-load floor. Drives the helper-text color so the user sees
  // the violation on keystroke (not only on Save submit).
  const minSocViolatesFloor =
    minSocFloorPercent != null &&
    formData.min_soc !== "" &&
    formData.min_soc != null &&
    !Number.isNaN(Number(formData.min_soc)) &&
    Number(formData.min_soc) <= minSocFloorPercent;

  const simulationName = sessionStorage.getItem("microgridDesign");
  const [selectedFile, setSelectedFile] = useState(null);

  // Which upload the form is waiting for. Each upload takes the next number
  // when it starts, and a switch of Device Type or storage mode takes one too
  // (resetUploadState). A reply is applied only while its number is still the
  // latest, so a file sent from the section the user has since left, or one
  // since replaced by another file, changes nothing on the screen.
  const uploadRef = useRef(0);

  // Put the upload state back as the form started: every section's upload
  // shares it. Called when Device Type or the storage mode changes, together
  // with withoutUpload on the form values, so one section's file can't satisfy
  // another's "file required" check, and an EV needs its own file again (its
  // SOC fields lock until that file's values arrive). An upload still in
  // flight belongs to the section just left: its spinner goes now, and its
  // reply is dropped when it arrives (see uploadRef).
  const resetUploadState = () => {
    uploadRef.current += 1;
    setSelectedFile(null);
    setUploadMessage("");
    setUploadWarnings([]);
    setEvSocFromCsv(!device);
    setUploading(false);
  };


  const CSV_INPUT_FILE_TYPE_MAP = {
    "csv-upload-ev": "ev_availability",
    "csv-upload-production": "solar",
    "csv-upload-consumption": "load",
    "csv-upload": "tariff",
  };

  // Handle file change for CSV file upload
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    const fileType = CSV_INPUT_FILE_TYPE_MAP[e.target.id] || "load";
    if (file && file.name.endsWith(".csv")) {
      const upload = ++uploadRef.current;
      const isCurrent = () => upload === uploadRef.current;
      setSelectedFile(file);
      setFormData((prevData) => ({
        ...prevData,
        csv_file_name: file.name,
      }));
      setUploading(true);
      const message = await uploadfile(file, fileType, isCurrent);
      // Superseded: the switch or the newer upload owns the spinner now.
      if (!isCurrent()) return;
      setUploadMessage(message);
      setUploading(false);

      // Clear any previous error if upload is successful
      if (message.type === "success") {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.csv_file_name;
          delete newErrors.uploadMessage;
          return newErrors;
        });
      }
    } else {
      toast.error("Please upload a valid .csv file");
    }
  };

  

  // Sends the file, then applies what csv-parse-metadata read from it, but
  // only while isCurrent() says the form is still waiting for this upload.
   const uploadfile = async (fileName, fileType, isCurrent) => {
    setUploadWarnings([]);
    try {
      const customerName = sessionStorage.getItem("storageAccountName");
      const customerEmail = sessionStorage.getItem("selectedCustomerEmail") ? sessionStorage.getItem("selectedCustomerEmail").replace(/[@.]/g, "-") : "default-simulation-name";
      const simulationName = sessionStorage.getItem("microgridDesign");
      const datasetName = encodeURIComponent(fileName.name);
      if (!token) {
        return {
          type: "error",
          text: "Missing access token. Please log in again.",
        };
      }
     

      const fileContent = await fileName.text();
      const result = await uploadFileApi({
        customerName,
        simulationName,
        datasetName,
        token,
        fileContent,
      });

      const csvFormData = new FormData();
      csvFormData.append("csv_file", fileName);
      
      const response = await fetch(
        `${PYTHON_API_BASE_URL}/csv-parse-metadata/?file_type=${fileType}`,
        {
          method: "POST",
          headers: {
              Authorization: `Bearer ${token}`,
              "X-User-ID": sessionStorage.getItem("UserID") || "",
          },
          body: csvFormData,
        }
      );
      if (!response.ok) {
        let serverMsg = "";
        try {
          const errBody = await response.json();
          serverMsg = errBody.error || errBody.message || "";
        } catch { /* response wasn't JSON */ }
        throw new Error(serverMsg || "Failed to parse CSV file.");
      }
      const data = await response.json();
      if (!isCurrent()) return null;
      const { start_date: startDate, end_date: endDate, data_stats: dataStats } = data;
      setUploadWarnings(csvWarnings(data));
      if (startDate && endDate) {
        setFormData((prevData) => ({
          ...prevData,
          start_date: startDate,
          end_date: endDate,
        }));
      }

      if (fileType === "ev_availability") {
        // An all-zero SOC column gives no values: the fields are cleared and
        // become editable (see evSocFromCsvStats). A reply without supported
        // stats is read the same way, so the SOC isn't left locked and blank.
        const { socFromCsv, ...soc } = evSocFromCsvStats(
          dataStats?.supported ? dataStats : null,
        );
        setEvSocFromCsv(socFromCsv);
        setFormData((prevData) => ({ ...prevData, ...soc }));
      }

      if (fileType === "solar" && dataStats?.supported) {
        setFormData((prevData) => ({
          ...prevData,
          min_power_charging: dataStats.min_value,
          max_power_charging: dataStats.max_value,
        }));
      }

      if (fileType === "load" && dataStats?.supported) {
        setFormData((prevData) => ({
          ...prevData,
          max_power_charging: dataStats.max_value,
        }));
      }

      return result;

    } catch (err) {
      console.error("Upload failed:", err);
      return {
        type: "error",
        text: `❌ ${err.message || "Network/server error occurred during CSV file parsing."}`,
      };
    }
  }

  // Warnings the admin returned for an uploaded file that was accepted, e.g.
  // an EV file with no SOC above zero ("set them on the device").
  const renderUploadWarnings = () =>
    uploadWarnings.length > 0 && (
      <div className="upload-message warning" role="status">
        {uploadWarnings.map((warning) => (
          <div key={warning}>⚠️ {warning}</div>
        ))}
      </div>
    );


  // Fetch customer's email from session storage when the component mounts
  useEffect(() => {
    const email = sessionStorage.getItem("selectedCustomerEmail");
    if (email) {
      setCustomer(email);
      const sanitizedEmail = email.replace(/[^a-zA-Z0-9]/g, "-");
      setCustomerSimulationName(simulationName);
      fetchStorageAccountName(selectedBranchId);
    } else {
      console.error("No customer email found in session storage.");
    }
  }, []);

  const fetchStorageAccountName = async (branchId) => {
    const API_URL = `${PYTHON_API_BASE_URL}/storage-account/?branch_id=${branchId}`;

    try {
      const response = await fetch(API_URL, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-User-ID": sessionStorage.getItem("UserID") || "",
        },
      });

      if (!response.ok)
        throw new Error("Failed to fetch storage account name.");

      const data = await response.json();
      setStorageAccountName(data.storage_account?.storage_account_name);
    } catch (error) {
      console.error("Error fetching storage account name:", error);
    }
  };

  // When editing an existing device, populate the form with its saved values.
  // Fractions (SOC, efficiency) are converted to percentages, and missing fields fall back to defaults.
  useEffect(() => {
    if (device) {
      const dischargingStrategy = device.discharging_strategy !== undefined && device.discharging_strategy !== null
        ? device.discharging_strategy
        : "unrestricted";
      
      setFormData({
        device_type: activeTab === "All" ? (device.deviceCategory || device.device_type || "Production") : activeTab,
        storage_mode: device.storage_mode || "",
        device_name: device.device_name || "",
        make_and_model: device.make_and_model || "",
        serial_number: device.serial_number || "",
        status: device.status || "connected",
        max_energy: device.max_energy ?? "",
        min_soc: device.min_soc != null ? fracToPct(device.min_soc) : "",
        max_soc: device.max_soc != null ? fracToPct(device.max_soc) : "",
        initial_soc: device.initial_soc != null ? fracToPct(device.initial_soc) : "",
        charging_efficiency_fraction:
          device.charging_efficiency_fraction != null ? fracToPct(device.charging_efficiency_fraction) : "",
        discharging_efficiency_fraction:
          dischargingStrategy === "forbidden" ? "" :
          (device.discharging_efficiency_fraction === null ||
          device.discharging_efficiency_fraction === undefined
            ? ""
            : fracToPct(device.discharging_efficiency_fraction)),
        avg_parasitic_load: device.avg_parasitic_load ?? "NA",
        fractional_self_loss: device.fractional_self_loss ?? "NA",
        average_energy_used_during_operation: (() => {
          const v = device.average_energy_used_during_operation;
          return (v !== undefined && v !== null && v !== "NA") ? String(v) : "";
        })(),
        max_power_charging: (device.max_power_charging != null && device.max_power_charging !== "NA")
          ? String(device.max_power_charging)
          : (device.peakPower ?? device.maxPower ?? ""),
        min_power_charging: (device.min_power_charging != null && device.min_power_charging !== "NA")
          ? String(device.min_power_charging)
          : "",
        max_power_discharging: dischargingStrategy === "forbidden" ? "" : (device.max_power_discharging ?? ""),

        daily_cycles: (device.storage_mode === "BESS" || device.storage_mode === "EV") ? "1" : (device.daily_cycles ?? ""),

        charging_strategy: device.charging_strategy,
        discharging_strategy: dischargingStrategy,
        production_type: device.production_type
          || device.generation_type
          || (device.yearly_degradation != null && device.yearly_degradation !== "NA" ? "Solar PV"
            : device.fuel_type != null && device.fuel_type !== "NA" ? "Fuel generator"
            : device.csv_file_name && device.csv_file_name !== "NA"
              && (device.device_type === "Production" || device.deviceCategory === "Production") ? "Solar PV"
            : ""),
        yearly_degradation: device.yearly_degradation ?? "NA",
        startup_cost: device.startup_cost ?? "NA",
        fuel_type: device.fuel_type || "NA",
        fuel_storage_size: device.fuel_storage_size ?? "NA",
        carbon_emissions: device.carbon_emissions ?? "NA",
        consumption: device.consumption ?? "NA",
        fuel_cost_per_kg: device.fuel_cost_per_kg ?? "NA",
        fuel_cost_escalator_fraction:
          device.fuel_cost_escalator_fraction ?? "NA",
        demand_charge: device.demand_charge ?? "NA",
        csv_file_name: device.csv_file_name || "NA",
        demand_charge_escalator_fraction:
          device.demand_charge_escalator_fraction ?? "NA",
        coupled_device_ids: Array.isArray(device.coupled_device_ids)
          ? device.coupled_device_ids
          : [],
      });
    }
  }, [device, activeTab]);

  // The coupling picker: which device kind the form currently describes, and
  // the devices on the meter it may be coupled to. Both follow the form's
  // own discriminators, so switching EV <-> BESS or Solar <-> Generator
  // re-lists the candidates (and drops any tick that is no longer a
  // supported pair at save time - deviceMutations filters).
  const couplableDeviceType = formValuesToDeviceType(formData);
  const couplingCandidates = couplableDeviceType
    ? listCouplingCandidates(sysconfigProp, {
        deviceType: couplableDeviceType,
        deviceId: device?.device_id || null,
      })
    : [];

  const coupledDevicesField = couplableDeviceType ? (
    <CoupledDevicesField
      deviceType={couplableDeviceType}
      candidates={couplingCandidates}
      selectedIds={formData.coupled_device_ids || []}
      strategies={{
        charging_strategy: formData.charging_strategy,
        discharging_strategy: formData.discharging_strategy,
      }}
      onChange={(ids) =>
        setFormData((prev) => ({ ...prev, coupled_device_ids: ids }))
      }
      onSetStrategy={(field, value) =>
        setFormData((prev) => ({ ...prev, [field]: value }))
      }
    />
  ) : null;

  // For Storage devices: clear discharging fields when strategy is "forbidden", and restore them when switching back
  useEffect(() => {
    if (formData.device_type !== "Storage") return;
    
    if (formData.discharging_strategy === "forbidden") {
      // Store current values before zeroing (only if they're non-zero)
      if (formData.max_power_discharging !== 0 && formData.max_power_discharging !== "") {
        priorDischargingValuesRef.current.max_power_discharging = formData.max_power_discharging;
      }
      if (formData.discharging_efficiency_fraction !== 0 && formData.discharging_efficiency_fraction !== "") {
        priorDischargingValuesRef.current.discharging_efficiency_fraction = formData.discharging_efficiency_fraction;
      }
      // Clear the disabled fields instead of showing a synthetic zero.
      setFormData(prev => ({
        ...prev,
        max_power_discharging: "",
        discharging_efficiency_fraction: ""
      }));
    } else if (formData.discharging_strategy && formData.discharging_strategy !== "") {
      // Restore prior values when leaving forbidden
      const prior = priorDischargingValuesRef.current;
      if (prior.max_power_discharging !== null || prior.discharging_efficiency_fraction !== null) {
        setFormData(prev => ({
          ...prev,
          max_power_discharging: prior.max_power_discharging ?? prev.max_power_discharging,
          discharging_efficiency_fraction: prior.discharging_efficiency_fraction ?? prev.discharging_efficiency_fraction
        }));
      }
    }
  }, [formData.discharging_strategy, formData.device_type]);

  // Generic onChange handler for all form inputs. Sanitizes numeric fields (decimal precision,
  // range clamping, length limits), applies storage-mode-specific defaults (EV vs BESS strategies),
  // and clears related validation errors on change.
  const handleChange = (e) => {
    const { id, value } = e.target;

    if (id === "device_type") {
      setErrors({}); // 👈 Clear all previous errors
      // Optionally reset form fields not relevant anymore
      setFormData((prevData) => ({
        ...prevData,
        device_type: activeTab,
        // Reset other dependent fields to default
        storage_mode: "",
        production_type: "",
        csv_file_name: "",
        startup_cost: "",
        min_power_charging: "",
        max_power_charging: "",
        discharging_efficiency_fraction: "",
        fuel_cost_escalator_fraction: "",
        fuel_cost_per_kg: "",
        consumption: "",
        carbon_emissions: "",
        fuel_storage_size: "",
        fuel_type: "",
        demand_charge: "",
        demand_charge_escalator_fraction: "",
      }));
      return;
    }

    // Declare sanitizedValue as a let to allow modification
    let sanitizedValue = value;
    let isValid = true;

    // Restrict specific fields to 2 decimal places
    if (
      [
        "startup_cost",
        "fuel_type",
        "fuel_storage_size",
        "fuel_storage_size",
        "fuel_cost_per_kg",
        "fuel_cost_escalator_fraction",
        "carbon_emissions",
        "consumption",

        "max_energy",
        "max_power_charging",
        "max_power_discharging",
        "min_power_charging",
        "daily_cycles",
        "yearly_degradation",
        "demand_charge",
        "demand_charge_escalator_fraction",
      ].includes(id)
    ) {
      const decimalPlaces = id === "yearly_degradation" ? 4 : 3;
      sanitizedValue = value.includes(".")
        ? value.split(".")[0] + "." + value.split(".")[1].slice(0, decimalPlaces)
        : value;
    }
    
    if (id === "storage_mode") {
      // If we're leaving current mode, remember the last entered discharging values
      // so we can restore them if the user comes back.
      if (formData.storage_mode && formData.storage_mode !== value) {
        const current = Number(formData.max_power_discharging);
        if (!Number.isNaN(current) && current > 0) {
          lastMaxPowerDischargingRef.current = current;
        }

        const currentEff = Number(formData.discharging_efficiency_fraction);
        if (!Number.isNaN(currentEff) && currentEff > 0) {
          lastDischargingEfficiencyRef.current = currentEff;
        }
      }

      // A new storage mode starts its upload afresh (see resetUploadState and
      // withoutUpload): an EV file doesn't follow to a BESS and back.
      resetUploadState();

      // For controlled <select>, the default must be set via state (not <option selected>).
      // When user chooses EV, default the charging strategy to Smart Charging (V1G).
      if (value === "EV") {
        setFormData((prev) => ({
          ...withoutUpload(prev),
          storage_mode: value,
          daily_cycles: "1",
          // Reset discharging fields when switching to EV
          max_power_discharging: "",
          discharging_efficiency_fraction: "",
          charging_strategy:
            !prev.charging_strategy || prev.charging_strategy === "NA"
              ? "smart_charging"
              : prev.charging_strategy,
          // EV defaults to unrestricted discharging
          discharging_strategy: "unrestricted",
          // A new EV's SOC comes from its file (or from the user, when the
          // file has none), never from a BESS's defaults.
          ...(!device && {
            min_soc: "",
            max_soc: "",
            initial_soc: "",
            charging_efficiency_fraction: 95,
            discharging_efficiency_fraction: 95,
          }),
        }));
      } else if (value === "BESS") {
        // Default both strategies to unrestricted for BESS (unless already set).
        setFormData((prev) => ({
          ...withoutUpload(prev),
          storage_mode: value,
          daily_cycles: "1",
          // If user is entering BESS for the first time (or coming from EV), restore previous value if available.
          max_power_discharging:
            prev.max_power_discharging && Number(prev.max_power_discharging) > 0
              ? prev.max_power_discharging
              : lastMaxPowerDischargingRef.current ?? "",
          discharging_efficiency_fraction:
            prev.discharging_efficiency_fraction &&
            Number(prev.discharging_efficiency_fraction) > 0
              ? prev.discharging_efficiency_fraction
              : lastDischargingEfficiencyRef.current ?? "",
          charging_strategy:
            !prev.charging_strategy || prev.charging_strategy === "NA"
              ? "unrestricted"
              : prev.charging_strategy,
          discharging_strategy:
            !prev.discharging_strategy || prev.discharging_strategy === "NA" || prev.discharging_strategy === "forbidden"
              ? "unrestricted"
              : prev.discharging_strategy,
          ...(!device && {
            min_soc: 10,
            max_soc: 90,
            initial_soc: 85,
            charging_efficiency_fraction: 95,
            discharging_efficiency_fraction: 95,
          }),
        }));
      } else {
        // Preserve previously selected strategies when switching away from EV/BESS.
        setFormData((prev) => ({ ...withoutUpload(prev), storage_mode: value }));
      }

      // Clear any validation errors tied to strategies when we auto-default
      // them, and the upload's errors, which belonged to the file just dropped.
      setErrors((prevErrors) => {
        if (
          !prevErrors?.charging_strategy &&
          !prevErrors?.discharging_strategy &&
          !prevErrors?.max_power_discharging &&
          !prevErrors?.discharging_efficiency_fraction &&
          !prevErrors?.csv_file_name &&
          !prevErrors?.uploadMessage
        )
          return prevErrors;
        const next = { ...prevErrors };
        delete next.charging_strategy;
        delete next.discharging_strategy;
        delete next.max_power_discharging;
        delete next.discharging_efficiency_fraction;
        delete next.csv_file_name;
        delete next.uploadMessage;
        return next;
      });

      return;
    }
    // Restrict to decimals where needed
    if (["avg_parasitic_load", "fractional_self_loss"].includes(id)) {
      sanitizedValue = value.includes(".")
        ? value.split(".")[0] +
          "." +
          value.split(".")[1].slice(0, id === "avg_parasitic_load" ? 3 : 5)
        : value;
    }

    // Ensure SOC values are between 0 and 100
    if (["min_soc", "max_soc", "initial_soc"].includes(id)) {
      const numericValue = Number(sanitizedValue);
      if (numericValue < 0 || numericValue > 100) {
        return;
      }
    }

    // Limit numeric fields to specific lengths
    if (
      ["max_energy", "max_power_charging"].includes(id) &&
      sanitizedValue.length > 12
    ) {
      return;
    }

    if (id === "max_power_discharging" && sanitizedValue.length > 13) {
      return; // Allow up to 13 characters for negative values
    }
    setErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      if (newErrors[id]) {
        delete newErrors[id];
      }
      return newErrors;
    });
    if (isValid) {
      setFormData({
        ...formData,
        [id]: sanitizedValue, // Update the specific field with sanitized value
      });
    }
  };

  // Validates all form fields based on device type (Storage, Production, Consumption, Transfer).
  // Checks required fields, numeric ranges, format constraints, and prompts the user to confirm unusual values (e.g. high power, atypical SOC range).
  const validateFields = async () => {
    const newErrors = {};
    let isValid = true;

    // Validation patterns for alphanumeric fields
    const alphaNumericRegex = /^(?=.*[a-zA-Z])(?=.*\d)[a-zA-Z0-9]{1,50}$/;
    const alphaNumericMakeRegex = /^[a-zA-Z0-9 ]{1,50}$/;

    // Validation for device type
    if (!formData.device_type) {
      newErrors.device_type = "Device type is required";
      isValid = false;
    }
    if (formData.device_type !== "Consumption") {
      const makeModelValue = formData.make_and_model.trim();
      const makeModelOptional = formData.storage_mode === "BESS" || formData.storage_mode === "EV" || formData.device_type === "Production";
      if (!makeModelValue && !makeModelOptional) {
        newErrors.make_and_model = "Make & Model is required";
        isValid = false;
      } else if (makeModelValue && !alphaNumericMakeRegex.test(makeModelValue)) {
        newErrors.make_and_model = "Please enter a valid Make & Model";
        isValid = false;
      } else if (makeModelValue && makeModelValue.length > 50) {
        newErrors.make_and_model = "Please enter a valid Make & Model";
        isValid = false;
      } else if (makeModelValue === "NA") {
        newErrors.make_and_model = "Please enter a valid Make & Model";
        isValid = false;
      }
    }
    // Validation for Device Name
    if (!formData.device_name.trim()) {
      newErrors.device_name = "Device Name is required";
      isValid = false;
    } else if (!alphaNumericMakeRegex.test(formData.device_name.trim())) {
      newErrors.device_name = "Please enter a valid Device Name";
      isValid = false;
    } else if (formData.device_name === "NA") {
      newErrors.device_name = "Please enter a valid Device Name";
      isValid = false;
    }

    // Validation for status
    if (!formData.status) {
      newErrors.status = "Status is required";
      isValid = false;
    }

    //validation for  dailyCycles


    if (formData.device_type === "Transfer") {
      if (
        formData.demand_charge === undefined ||
        formData.demand_charge === ""
      ) {
        newErrors.demand_charge = "Demand Charge is required";
        isValid = false;
      } else if (
        isNaN(formData.demand_charge) ||
        Number(formData.demand_charge) < 0 ||
        Number(formData.demand_charge) > 1
      ) {
        newErrors.demand_charge = "Demand Charge must be between 0 and 1";
        isValid = false;
      }
      if (
        formData.demand_charge_escalator_fraction === undefined ||
        formData.demand_charge_escalator_fraction === ""
      ) {
        newErrors.demand_charge_escalator_fraction =
          "Demand Charge Escalator fraction is required";
        isValid = false;
      }
      if (!formData.csv_file_name || uploadMessage.type == "error") {
        newErrors.csv_file_name = "Utility tariff Data File is required.";
        isValid = false;
      }
      if (uploadMessage.type === "error") {
        newErrors.uploadMessage = "please upload a valid file.";
        isValid = false;
      }
    }

    // Additional validations for storage devices
    if (formData.device_type === "Storage") {
      // Validation for storage mode
      if (!formData.storage_mode) {
        newErrors.storage_mode = "Storage type is required (EV/BESS).";
        isValid = false;
      }

      // Validation for max power charging (must be positive)
      if (!formData.max_power_charging) {
        newErrors.max_power_charging = "Max power charging is required";
        isValid = false;
      } else if (formData.max_power_charging === undefined || formData.max_power_charging === "") {
        newErrors.max_power_charging = "Max power charging is required";
        isValid = false;
      } else if (!/^\d+(\.\d+)?$/.test(formData.max_power_charging)) {
        newErrors.max_power_charging = "Max power charging must be a valid number";
        isValid = false;
      } else if (Number(formData.max_power_charging) < 1) {
        newErrors.max_power_charging = "Max power charging must be a positive or one";
        isValid = false;
      }

      // Validate Average Parasitic Load
      if (formData.avg_parasitic_load === undefined || formData.avg_parasitic_load === null || formData.avg_parasitic_load === "" || formData.avg_parasitic_load == "NA") {
        newErrors.avg_parasitic_load = "Average Parasitic Load is required";
        isValid = false;
      } else if (!/^\d+(\.\d+)?$/.test(formData.avg_parasitic_load)) {
        newErrors.avg_parasitic_load = "Average Parasitic Load must be a valid number";
        isValid = false;
      } else if (Number(formData.avg_parasitic_load) < 0) {
        newErrors.avg_parasitic_load = "Parasitic Load must be positive";
        isValid = false;
      } else if (Number(formData.avg_parasitic_load) > 100) {
        newErrors.avg_parasitic_load =
          "Parasitic Load must be less than or equal to 100";
        isValid = false;
      }

      // Confirm unusual values (e.g. high power, atypical SOC range)
      if (isValid && Number(formData.avg_parasitic_load) > 1) {
        const confirmParasitic = window.confirm(
          `You entered an average parasitic load of ${formData.avg_parasitic_load} (greater than 1).\n\n` +
            `Recommended: keep it below 1. Are you sure you want to continue?`,
        );
        if (!confirmParasitic) return false;
      }

      // Soft warning for EV (BESS is hard-blocked below): warn, but allow
      // override, when Min SOC may be too low to absorb the parasitic drain
      // without producing a negative SOC during simulation. Threshold is
      // computed in `minSocFloorPercent` (top of component). A blank or
      // non-numeric Min SOC never violates it; the SOC checks below report
      // those. Only a Min SOC read from the file is called CSV-derived.
      if (isValid && formData.storage_mode === "EV" && minSocViolatesFloor) {
        const proceed = window.confirm(
          `The ${evSocFromCsv ? "CSV-derived " : ""}Min SOC ` +
            `(${Number(formData.min_soc)}%) is below the parasitic-load ` +
            `floor of ${minSocFloorPercent.toFixed(1)}%.\n\n` +
            `This may cause a negative SOC during simulation. Continue anyway?`,
        );
        if (!proceed) return false;
      }

      // Validate Fractional Self Loss
      if ( formData.fractional_self_loss === undefined || formData.fractional_self_loss === null || formData.fractional_self_loss === "" || formData.fractional_self_loss == "NA" ) {
        newErrors.fractional_self_loss = "Fractional Self Loss is required";
        isValid = false;
      } else if (!/^\d+(\.\d+)?$/.test(formData.fractional_self_loss)) {
        newErrors.fractional_self_loss = "Fractional Self Loss must be a valid number";
        isValid = false;
      } else if ( Number(formData.fractional_self_loss) < 0 || Number(formData.fractional_self_loss) > 0.001 ) {
        newErrors.fractional_self_loss = "Fractional Self Loss must be between 0 and 0.001";
        isValid = false;
      }

      // Validate Charging Efficiency
      if ( !formData.charging_efficiency_fraction || formData.charging_efficiency_fraction == "NA" ) {
        newErrors.charging_efficiency_fraction = "Charging Efficiency % is required.";
        isValid = false;
      } else if ( Number(formData.charging_efficiency_fraction) < 1 || Number(formData.charging_efficiency_fraction) > 100 ) {
        newErrors.charging_efficiency_fraction = "Charging Efficiency must be between 1 - 100.";
        isValid = false;
      }

      // Only require/validate discharging efficiency when discharging is NOT forbidden
      if (formData.discharging_strategy !== "forbidden") {
        const dischargingEfficiency = formData.discharging_efficiency_fraction;
        if (
          dischargingEfficiency === "" ||
          dischargingEfficiency === null ||
          dischargingEfficiency === undefined ||
          dischargingEfficiency === "NA"
        ) {
          newErrors.discharging_efficiency_fraction = "Discharging Efficiency % is required.";
          isValid = false;
        } else if (Number.isNaN(Number(dischargingEfficiency))) {
          newErrors.discharging_efficiency_fraction = "Discharging Efficiency % must be a valid number.";
          isValid = false;
        } else if (Number(dischargingEfficiency) < 1 || Number(dischargingEfficiency) > 100) {
          newErrors.discharging_efficiency_fraction = "Discharging Efficiency must be between 1 - 100.";
          isValid = false;
        }
      }
     
      if (formData.storage_mode === "EV") {
        if (!formData.csv_file_name) {
          newErrors.csv_file_name = "EV Data File is required.";
          isValid = false;
        }
        if (uploadMessage && uploadMessage.type === "error") {
          newErrors.uploadMessage = "please upload a valid file.";
          isValid = false;
        }
      }

      // Max Power Discharging Validation - skip when discharging is forbidden
      if (formData.discharging_strategy !== "forbidden") {
        if (
          formData.max_power_discharging === "" ||
          formData.max_power_discharging === null ||
          formData.max_power_discharging === undefined ||
          formData.max_power_discharging === "NA"
        ) {
          newErrors.max_power_discharging =
            "Max discharging power is required.";
          isValid = false;
        } else if (Number(formData.max_power_discharging) < 0) {
          newErrors.max_power_discharging =
            "Max discharging power must be a positive or zero value.";
          isValid = false;
        }
      }

      if (!formData.max_energy || formData.max_energy == "NA") {
        newErrors.max_energy = "Max energy is required";
        isValid = false;
      } else if (!/^\d+(\.\d+)?$/.test(formData.max_energy)) {
        newErrors.max_energy = "Max energy must be a valid number";
        isValid = false;
      } else if (Number(formData.max_energy) < 0) {
        newErrors.max_energy = "Max energy must be a positive number";
        isValid = false;
      }

      // SOC required/range validation: always for BESS, and for an EV whose
      // SOC the user typed in or is editing. SOC read from the EV's file is
      // disabled and taken as the file gives it. The EV and BESS rules differ
      // (see socErrors).
      if (formData.storage_mode !== "EV" || !evSocFromCsv) {
        const socFieldErrors = socErrors(formData, {
          storageMode: formData.storage_mode,
        });
        if (Object.keys(socFieldErrors).length > 0) {
          Object.assign(newErrors, socFieldErrors);
          isValid = false;
        }
      }

      // Hard block for BESS: Min SOC must leave a buffer above the energy
      // drained by the parasitic load each hour, otherwise the simulation
      // can compute a negative SOC and fail with an OptimizationException.
      // No error message is added here -- the inline helper text under Min SOC
      // (driven by `minSocViolatesFloor`) turns red to communicate the issue.
      if (formData.storage_mode === "BESS" && minSocViolatesFloor) {
        isValid = false;
      }


      if (formData.storage_mode !== "BESS" && formData.storage_mode !== "EV") {
        if (!formData.daily_cycles) {
          newErrors.daily_cycles = "Daily Cycles is required";
          isValid = false;
        } else if (
          Number(formData.daily_cycles) < 0 ||
          Number(formData.daily_cycles) > 12
        ) {
          newErrors.daily_cycles = "Daily Cycles must be between 0 and 12";
          isValid = false;
        }
      }

      if (!formData.charging_strategy) {
        newErrors.charging_strategy = "Charging Strategy is required";
        isValid = false;
      }
      // Discharging strategy is always required for Storage devices
      if (!formData.discharging_strategy) {
        newErrors.discharging_strategy = "Discharging Strategy is required";
        isValid = false;
      }

      if (
        isValid &&
        (Number(formData.max_power_charging) > 10000 ||
          (formData.discharging_strategy !== "forbidden" && Number(formData.max_power_discharging) > 10000))
      ) {
        const confirmPower = window.confirm(
          `You entered high charging/discharging power:\n\n` +
            `Max Charging: ${formData.max_power_charging} kW\n` +
            `Max Discharging: ${formData.max_power_discharging} kW\n\n` +
            `Typical values are <= 10,000 kW.\nAre you sure you want to continue?`,
        );

        if (!confirmPower) {
          isValid = false; // cancel form submission
        }
      }

      if (formData.storage_mode === "EV") {
        const v = formData.average_energy_used_during_operation;
        if (v === "" || v == null || v === "NA") {
          newErrors.average_energy_used_during_operation = "Average Away Discharge is required";
          isValid = false;
        } else if (Number(v) < 0 || Number(v) > 100) {
          newErrors.average_energy_used_during_operation = "Average Away Discharge must be between 0 and 100";
          isValid = false;
        }
      }
    }

    if (formData.device_type === "Production") {
      // Solar
      if (formData.production_type === "Solar PV") {
        if (!formData.csv_file_name) {
          newErrors.csv_file_name = "Production Data File is required.";
          isValid = false;
        }
        if (uploadMessage && uploadMessage.type === "error") {
          newErrors.uploadMessage = "Please upload a valid  file.";
          isValid = false;
        }
      }
      if (formData.production_type === "Solar PV") {
        if (formData.yearly_degradation === "" || formData.yearly_degradation === undefined) {
          newErrors.yearly_degradation = "yearly degradation is required.";
          isValid = false;
        } else if (
          formData.yearly_degradation < 0 ||
          formData.yearly_degradation > 0.01
        ) {
          newErrors.yearly_degradation =
            "yearly degradation must be between 0 and 0.01";
          isValid = false;
        }
      }
      if (!formData.production_type) {
        newErrors.production_type = "Production type is required";
        isValid = false;
      }

      
      // Startup Cost Validation (Fuel generator only)
      if (formData.production_type === "Fuel generator") {
        if (
          formData.startup_cost === undefined ||
          formData.startup_cost === "" ||
          formData.startup_cost === "NA"
        ) {
          newErrors.startup_cost = "Startup cost is required";
          isValid = false;
        } else if (!/^\d+(\.\d+)?$/.test(formData.startup_cost)) {
          newErrors.startup_cost = "Startup cost must be a valid number";
          isValid = false;
        } else if (Number(formData.startup_cost) < 0) {
          newErrors.startup_cost = "Startup cost cannot be negative";
          isValid = false;
        }
      }

// Max Power Charging Validation
if (formData.max_power_charging === undefined || formData.max_power_charging === "") {
  newErrors.max_power_charging = "Max power charging is required";
  isValid = false;
} else if (!/^\d+(\.\d+)?$/.test(formData.max_power_charging)) {
  newErrors.max_power_charging = "Max power charging must be a valid number";
  isValid = false;
} else if (Number(formData.max_power_charging) < 0) {
  newErrors.max_power_charging = "Max power charging must be zero or a positive number";
  isValid = false;
}

// Min Power Charging Validation
if (formData.min_power_charging === undefined || formData.min_power_charging === "") {
  newErrors.min_power_charging = "Min power charging is required";
  isValid = false;
} else if (!/^\d+(\.\d+)?$/.test(formData.min_power_charging)) {
  newErrors.min_power_charging = "Min power charging must be a valid number";
  isValid = false;
} else if (Number(formData.min_power_charging) < 0) {
  newErrors.min_power_charging = "Min power charging must be zero or a positive number";
  isValid = false;
}

// Max > Min Validation
if (
  formData.max_power_charging &&
  formData.min_power_charging &&
  /^\d+(\.\d+)?$/.test(formData.max_power_charging) &&
  /^\d+(\.\d+)?$/.test(formData.min_power_charging) &&
  Number(formData.max_power_charging) <= Number(formData.min_power_charging)
) {
  newErrors.max_power_charging = "Max operating power must be greater than Min operating power";
  isValid = false;
}

      // Fuel Generator
      if (formData.production_type === "Fuel generator") {
        if (!formData.fuel_type) {
          newErrors.fuel_type = "Fuel Type is required.";
          isValid = false;
        }

     if (formData.fuel_storage_size === "" || formData.fuel_storage_size === undefined) {
  newErrors.fuel_storage_size = "Fuel storage size is required.";
  isValid = false;
} else if (!/^\d+(\.\d+)?$/.test(formData.fuel_storage_size)) {
  newErrors.fuel_storage_size = "Fuel storage size must be a valid number.";
  isValid = false;
} else if (Number(formData.fuel_storage_size) <= 0) {
  newErrors.fuel_storage_size = "Fuel storage size must be greater than 0.";
  isValid = false;
}



        if (
          formData.fuel_cost_per_kg === undefined ||
          formData.fuel_cost_per_kg === "" 
        ) {
          newErrors.fuel_cost_per_kg = "Fuel cost is required.";
          isValid = false;
        } else if (!/^\d+(\.\d+)?$/.test(formData.fuel_cost_per_kg)) {
          newErrors.fuel_cost_per_kg = "Fuel cost must be a valid number.";
          isValid = false;
        } else if (Number(formData.fuel_cost_per_kg) <= 0) {
          /* Zero is rejected, not just negative. A generator priced at $0/kg
           * is free power to the optimizer: it out-competes the grid at every
           * hour, and the Production card's Fuel row -- generation value less
           * production cost -- reads $0 on a machine that plainly burns fuel.
           * Every fueled generator must carry a price. */
          newErrors.fuel_cost_per_kg =
            "Fuel cost must be greater than 0.";
          isValid = false;
        }

        if (
          formData.carbon_emissions === undefined ||
          formData.carbon_emissions === "" 
        ) {
          newErrors.carbon_emissions = "Carbon emissions are required.";
          isValid = false;
        } else if (!/^\d+(\.\d+)?$/.test(formData.carbon_emissions)){
  newErrors.carbon_emissions = "Carbon emissions must be a valid number.";
  isValid = false;
} else if (Number(formData.carbon_emissions) < 0) {
          newErrors.carbon_emissions =
            "Carbon emissions cannot be negative.";
          isValid = false;
        }

        if (
          formData.consumption === undefined ||
          formData.consumption === "" 
        ) {
          newErrors.consumption = "Fuel consumption is required.";
          isValid = false;
        }else if (!/^\d+(\.\d+)?$/.test(formData.consumption)) {
  newErrors.consumption = "Fuel Consumption must be a valid number.";
  isValid = false;
}else if (Number(formData.consumption) < 0) {
          newErrors.consumption = "Fuel consumption cannot be negative.";
          isValid = false;
        }

        if (
          formData.fuel_cost_escalator_fraction === undefined ||
          formData.fuel_cost_escalator_fraction === "" 
        ) {
          newErrors.fuel_cost_escalator_fraction =
            "Fuel cost escalator fraction is required.";
          isValid = false;
        }
        else if (!/^\d+(\.\d+)?$/.test(formData.fuel_cost_escalator_fraction)) {
  newErrors.fuel_cost_escalator_fraction = "Fuel cost escalator must be a valid number.";
  isValid = false;
}  else if (
          Number(formData.fuel_cost_escalator_fraction) < 0 ||
          Number(formData.fuel_cost_escalator_fraction) > 0.15
        ) {
          newErrors.fuel_cost_escalator_fraction =
            "Fuel cost escalator must be a number between 0 and 0.15.";
          isValid = false;
        }
      }
    }

    if (formData.device_type === "Consumption") {
      if (!formData.csv_file_name) {
        newErrors.csv_file_name = "Building Load Data File is required.";
        isValid = false;
      }
      if (uploadMessage && uploadMessage.type === "error") {
        newErrors.uploadMessage = "Please upload a valid file.";
        isValid = false;
      }
      // Validation for max power charging (must be positive)
      if (formData.max_power_charging === undefined || formData.max_power_charging === "") {
  newErrors.max_power_charging = "Max power charging is required";
  isValid = false;
} else if (!/^\d+(\.\d+)?$/.test(formData.max_power_charging)) {
  newErrors.max_power_charging = "Max power charging must be a valid number";
  isValid = false;
} else if (Number(formData.max_power_charging) < 1) {
  newErrors.max_power_charging = "Max power charging must be at least 1";
  isValid = false;
}

    }

    // console.log(newErrors);
    setErrors(newErrors);
    return isValid;
  };

  const validateDevice = async (formData) => {
    const newErrors = {};

    try {
      const data = sysconfigProp || {};
      const existingNames = [];
      const meters = data?.Microgrid?.meters || [];
      meters.forEach((meter) => {
        (meter.evs || []).forEach((ev) => existingNames.push((ev.name || "").toLowerCase()));
        (meter.batteries || []).forEach((b) => existingNames.push((b.name || "").toLowerCase()));
        (meter.uncontrollable_loads || []).forEach((c) => existingNames.push((c.name || "").toLowerCase()));
        (meter.solar_arrays || []).forEach((s) => existingNames.push((s.name || "").toLowerCase()));
        (meter.fueled_generators || []).forEach((g) => existingNames.push((g.name || "").toLowerCase()));
        if (meter.name) existingNames.push((meter.name || "").toLowerCase());
      });

      const newNameLower = (formData.device_name || "").toLowerCase();
      const currentNameLower = device ? (device.device_name || "").toLowerCase() : null;
      const nameExists = existingNames.some(
        (name) => name === newNameLower && (currentNameLower === null || name !== currentNameLower)
      );

      if (nameExists) {
        toast.error("Device name already exists!");
        newErrors.device_name = "Device name already exists!";
        setErrors(newErrors);
        return false;
      }

      return true;
    } catch (err) {
      return true;
    }
  };
  

  

  const handleSave = async () => {
    const isFormValid = await validateFields();
    const validDevice = await validateDevice(formData);
    if (isFormValid && validDevice && customer && token) {
      setIsSaving(true);
      setShowConfirm(true);

      try {
        if (!device) {
          await addDeviceProp(formData);
          onSave();
          onClose();
        } else {
          const deviceId = device.device_id;
          if (!deviceId) {
            toast.error("This device was modified by another user. Please refresh.");
            return;
          }
          await updateDeviceProp(deviceId, formData);
          onSave();
          onClose();
        }
      } catch (error) {
        console.error("Error saving device:", error);
        toast.error("Error saving device. Please try again.");
      } finally {
        setIsSaving(false);
      }
    }
  };

  return (
    <div className="add-device-popup-container">
      <div className="add-device-content-wrapper">
        <div className="add-device-header-container">
          <h3 className="add-device-title">
            {device ? "Edit Device" : "Add New Device"}
          </h3>
          <button
            className="add-device-close-button"
            onClick={onClose}
            aria-label="Close"
          >
            &times;
          </button>
        </div>
        <div className="add-device-content-container">

          {/* Device Type */}
          <div className="add-device-form-group">
            <label htmlFor="device_type">
              Device Type: <span className="required-asterisk">*</span>
            </label>
            {activeTab === "All" ? (
              <select
                id="device_type"
                value={formData.device_type}
                disabled={!!device}
                onChange={(e) => {
                  setErrors({});
                  // Every section's upload shares the same upload state (file
                  // name, message, dates, warnings), so drop the last file's
                  // when its section goes away, with the values it filled in.
                  resetUploadState();
                  setFormData((prevData) => ({
                    ...withoutUpload(prevData),
                    ...UPLOAD_FILLED_VALUES_BLANK,
                    device_type: e.target.value,
                    storage_mode: "",
                    production_type: "",
                  }));
                }}
              >
                <option value="Production">Production</option>
                <option value="Consumption">Consumption</option>
                <option value="Storage">Storage</option>
              </select>
            ) : (
              <input
                type="text"
                id="device_type"
                defaultValue={activeTab}
                readOnly
              />
            )}
            {errors.device_type && (
              <span className="add-device-error-message">
                {errors.device_type}
              </span>
            )}
          </div>

          <hr className="form-section-divider" />


          {formData.device_type === "Storage" && (
            <>
              {/* Storage Type Selection */}
              <div className="add-device-form-group">
                <label>
                  Storage Type: <span className="required-asterisk">*</span>
                </label>
                <select
                  id="storage_mode"
                  value={formData.storage_mode}
                  disabled={!!device}
                  onChange={handleChange}
                >
                  <option value="" disabled>
                    -- Select EV or BESS --
                  </option>
                  <option value="EV">EV</option>
                  <option value="BESS">BESS</option>
                </select>
                {errors.storage_mode && (
                  <span className="add-device-error-message">
                    {errors.storage_mode}
                  </span>
                )}
              </div>

              <hr className="form-section-divider" />

              {/* EV Storage Mode */}
              {formData.storage_mode === "EV" && (

                <>
                  {/* Device Name */}
                  <div className="add-device-form-group">
                    <label htmlFor="device_name">
                      Device Name: <span className="required-asterisk">*</span>
                    </label>
                    <input
                      type="text"
                      maxLength={50}
                      id="device_name"
                      value={formData.device_name}
                      onChange={handleChange}
                      placeholder="Electrical Vehicle "
                    />
                    {errors.device_name && (
                      <span className="add-device-error-message">
                        {errors.device_name}
                      </span>
                    )}
                  </div>

                  {/* Make and Model */}
                  <div className="add-device-form-group">
                    <label htmlFor="device_name">
                      Make and Model:
                    </label>
                    <input
                      type="text"
                      maxLength={50}
                      id="make_and_model"
                      value={formData.make_and_model}
                      onChange={handleChange}
                      placeholder="Tesla Model Y"
                    />
                    {errors.make_and_model && (
                      <span className="add-device-error-message">
                        {errors.make_and_model}
                      </span>
                    )}
                  </div>

                  {/* Serial Number */}
                  <div className="add-device-form-group">
                    <label htmlFor="serial_number">
                      Serial Number: 
                    </label>
                    <input
                      type="text"
                      id="serial_number"
                      maxLength={50}
                      value={formData.serial_number}
                      onChange={handleChange}
                      placeholder="SN8392"
                    />
                    {errors.serial_number && (
                      <span className="add-device-error-message">
                        {errors.serial_number}
                      </span>
                    )}
                  </div>

                  {/* Status */}
                  <div className="add-device-form-group">
                    <label htmlFor="status">
                      Status: <span className="required-asterisk">*</span>
                    </label>
                    <select id="status" value={formData.status} onChange={handleChange}>
                      <option value="" disabled>
                        -- Select Status --
                      </option>
                      <option value="connected">Connected</option>
                      <option value="disconnected">Disconnected</option>
                    </select>
                    {errors.status && (
                      <span className="add-device-error-message">{errors.status}</span>
                    )}
                  </div>

                  {/* Charging Strategy */}
                  <div className="add-device-form-group">
                    <label htmlFor="charging_strategy">
                      Charging Strategy{" "}
                      <span className="required-asterisk">*</span>
                    </label>
                    <select
                      id="charging_strategy"
                      value={formData.charging_strategy}
                      onChange={handleChange}
                    >
                      <option value="" disabled>
                        -- Select Charging Strategy --
                      </option>
                      <option value="smart_charging">Smart Charging (V1G)</option>
                      <option value="generator_only">Generator Only</option>
                      <option value="prioritize_departure_readiness">Departure Readiness</option>
                      {/* <option value="unrestricted">Unrestricted</option> */}
                    </select>
                    {errors.charging_strategy && (
                      <span className="add-device-error-message">
                        {errors.charging_strategy}
                      </span>
                    )}
                  </div>

                  {/* EV Discharging Strategy */}
                  <div className="add-device-form-group">
                    <label htmlFor="discharging_strategy">
                      Discharging Strategy{" "}
                      <span className="required-asterisk">*</span>
                    </label>
                    <select
                      id="discharging_strategy"
                      value={formData.discharging_strategy}
                      onChange={handleChange}
                    >
                      <option value="" disabled>
                        -- Select Discharging Strategy --
                      </option>
                      <option value="unrestricted">Vehicle to Grid (V2G)</option>
                      <option value="building_load_only">Vehicle to Building (V2B)</option>
                      <option value="forbidden">Forbidden</option>
                    </select>
                    {errors.discharging_strategy && (
                      <span className="add-device-error-message">
                        {errors.discharging_strategy}
                      </span>
                    )}
                  </div>

                  <hr className="form-section-divider" />

                  {/* Max Energy */}
                  <div className="add-device-form-group">
                    <label htmlFor="max_energy">
                      Max Energy: <span className="required-asterisk">*</span>
                    </label>
                    <div className="input-group input-group-unified-focus">
                      <input
                        type="text"
                        className="form-control"
                        id="max_energy"
                        value={formData.max_energy}
                        onChange={handleChange}
                        placeholder="Max Energy"
                        step="0.01"
                        min="0"
                      />
                      <span className="input-group-text">kWh</span>
                    </div>
                    {errors.max_energy && (
                      <span className="add-device-error-message">
                        {errors.max_energy}
                      </span>
                    )}
                  </div>

                  {/* Max Power Charging and Max Power Discharging */}
                  <div className="add-device-form-group-horizontal">

                    {/* Max Power Charging */}
                    <div className="add-device-form-group">
                      <label htmlFor="max_power_charging">
                        Max Power Charging: <span className="required-asterisk">*</span>
                      </label>
                      <div className="input-group input-group-unified-focus">
                        <input
                          type="text"
                          className="form-control"
                          id="max_power_charging"
                          value={formData.max_power_charging}
                          onChange={handleChange}
                          placeholder="Max Power Charging"
                          step="0.01"
                          min="0"
                        />
                        <span className="input-group-text">kW</span>
                      </div>
                      {errors.max_power_charging && (
                        <span className="add-device-error-message">
                          {errors.max_power_charging}
                        </span>
                      )}
                    </div>




                    {/* Max Power Discharging */}
                    <div className="add-device-form-group">
                      <label htmlFor="max_power_discharging">
                        Max Power Discharging:
                        {!isDischargeForbidden && (
                          <span className="required-asterisk"> *</span>
                        )}
                      </label>
                      <div className="input-group input-group-unified-focus">
                        <input
                          type="text"
                          className="form-control"
                          id="max_power_discharging"
                          value={formData.max_power_discharging}
                          onChange={handleChange}
                          placeholder="Max Power Discharging"
                          step="0.01"
                          min="0"
                          disabled={isDischargeForbidden}
                          style={
                            isDischargeForbidden
                              ? { backgroundColor: "#e9ecef", cursor: "not-allowed" }
                              : undefined
                          }
                        />
                        <span className="input-group-text">kW</span>
                      </div>
                      {errors.max_power_discharging && (
                        <span className="add-device-error-message">
                          {errors.max_power_discharging}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Charging and discharging efficiency */}
                  <div className="add-device-form-group-horizontal">

                    {/* Charging Efficiency */}
                    <div className="add-device-form-group">
                      <label htmlFor="charging_efficiency_fraction">
                        Charging Efficiency: <span className="required-asterisk">*</span>
                      </label>
                      <div className="input-group input-group-unified-focus">
                        <input
                          type="text"
                          className="form-control"
                          id="charging_efficiency_fraction"
                          value={formData.charging_efficiency_fraction}
                          onChange={handleChange}
                          placeholder="Charging Efficiency"
                          step="0.01"
                          min="0"
                        />
                        <span className="input-group-text">%</span>
                      </div>
                      {errors.charging_efficiency_fraction && (
                        <span className="add-device-error-message">
                          {errors.charging_efficiency_fraction}
                        </span>
                      )}
                    </div>

                    {/* Discharging Efficiency */}
                    <div className="add-device-form-group">
                      <label htmlFor="discharging_efficiency_fraction">
                        Discharging Efficiency:
                        {!isDischargeForbidden && (
                          <span className="required-asterisk"> *</span>
                        )}
                      </label>
                      <div className="input-group input-group-unified-focus">
                        <input
                          type="text"
                          className="form-control"
                          id="discharging_efficiency_fraction"
                          value={formData.discharging_efficiency_fraction}
                          onChange={handleChange}
                          placeholder="Discharging Efficiency"
                          step="0.01"
                          min="0"
                          max="100"
                          disabled={isDischargeForbidden}
                          style={
                            isDischargeForbidden
                              ? { backgroundColor: "#e9ecef", cursor: "not-allowed" }
                              : undefined
                          }
                        />
                        <span className="input-group-text">%</span>
                      </div>
                      {errors.discharging_efficiency_fraction && (
                        <span className="add-device-error-message">
                          {errors.discharging_efficiency_fraction}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* EV Availability Data File + derived SOC fields */}
                  <div className="add-device-form-group" style={{ marginBottom: 0 }}>
                    <label>
                      EV Availability Data File (.csv) <span className="required">*</span>
                    </label>
                  </div>
                  <div className="csv-derived-group">
                    <div className="add-device-form-group">
                        <div className="file-input-container">
                          <div className="file-name-box">
                            <span
                              className={`file-name-text ${
                                formData.csv_file_name ? "file-selected" : "no-file"
                              }`}
                            >
                              {formData.csv_file_name || "No file selected"}
                            </span>
                            {uploading && <div className="upload-spinner" />}
                          </div>

                          <label htmlFor="csv-upload-ev" className="add-device-upload-button">
                            Select File
                          </label>

                          <input
                            id="csv-upload-ev"
                            type="file"
                            accept=".csv"
                            style={{ display: "none" }}
                            onChange={handleFileChange}
                          />
                        </div>

                        {errors.csv_file_name && (
                          <span className="add-device-error-message">
                            {errors.csv_file_name}
                          </span>
                        )}

                        {uploadMessage && (
                          <div
                            className={`upload-message ${
                              uploadMessage.type === "success" ? "success" : "error"
                            }`}
                          >
                            {uploadMessage.text}
                          </div>
                        )}
                        {renderUploadWarnings()}

                        {errors.uploadMessage && (
                          <span className="add-device-error-message">
                            {errors.uploadMessage}
                          </span>
                        )}

                    </div>

                    {/* Min SOC and Max SOC and Initial SOC */}
                    <div className="add-device-form-group-horizontal">

                      {/* Min SOC */}
                      <div className={"add-device-form-group" + (evSocFromCsv ? " disabled-field-tooltip" : "")}
                        data-tooltip={evSocFromCsv ? "Value is derived from the uploaded CSV file" : undefined}
                      >
                        <label>
                          Minimum SOC: <span className="required-asterisk">*</span>
                        </label>
                        <div className="input-group input-group-unified-focus">
                          <input
                            type="text"
                            className="form-control"
                            id="min_soc"
                            placeholder="Min SoC"
                            min="0"
                            max="100"
                            onInvalid={(e) => e.preventDefault()}
                            step="1"
                            value={formData.min_soc}
                            disabled={evSocFromCsv}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                min_soc: e.target.value,
                              })
                            }
                          />
                          <span className="input-group-text">%</span>
                        </div>
                        {errors.min_soc && (
                          <span className="add-device-error-message">
                            {errors.min_soc}
                          </span>
                        )}
                      </div>

                      {/* Max SOC */}
                      <div className={"add-device-form-group" + (evSocFromCsv ? " disabled-field-tooltip" : "")}
                        data-tooltip={evSocFromCsv ? "Value is derived from the uploaded CSV file" : undefined}
                      >
                        <label>
                          Maximum SOC: <span className="required-asterisk">*</span>
                        </label>
                        <div className="input-group input-group-unified-focus">
                          <input
                            type="text"
                            className="form-control"
                            id="max_soc"
                            placeholder="Max SoC"
                            min="1"
                            max="100"
                            step="1"
                            value={formData.max_soc ??""}
                            disabled={evSocFromCsv}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                max_soc: e.target.value,
                              })
                            }
                          />
                          <span className="input-group-text">%</span>
                        </div>
                        
                        {errors.max_soc && (
                          <span className="add-device-error-message">
                            {errors.max_soc}
                          </span>
                        )}
                      </div>

                      {/* Initial SOC */}
                      <div className={"add-device-form-group" + (evSocFromCsv ? " disabled-field-tooltip" : "")}
                        data-tooltip={evSocFromCsv ? "Value is derived from the uploaded CSV file" : undefined}
                      >
                        <label>
                          Initial SOC: <span className="required-asterisk">*</span>
                        </label>
                        <div className="input-group input-group-unified-focus">
                          <input
                            type="text"
                            className="form-control"
                            id="initial_soc"
                            placeholder="Initial SoC"
                            min="0"
                            max="100"
                            step="1"
                            value={formData.initial_soc}
                            disabled={evSocFromCsv}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                initial_soc: e.target.value,
                              })
                            }
                          />
                          <span className="input-group-text">%</span>
                        </div>

                        {errors.initial_soc && (
                          <span className="add-device-error-message">
                            {errors.initial_soc}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <hr className="form-section-divider" />

                  {/* Average Parasitic Load and Fractional Efficiency Loss */}
                  <div className="add-device-form-group-horizontal">
                    <div className="add-device-form-group">
                      <label htmlFor="avg_parasitic_load">
                        Average Parasitic Load:
                        <span className="required-asterisk">*</span>
                      </label>
                      <div className="input-group input-group-unified-focus">
                        <input
                          type="text"
                          className="form-control"
                          id="avg_parasitic_load"
                          value={formData.avg_parasitic_load}
                          onChange={handleChange}
                          placeholder="Enter Average Parasitic Load"
                          step="0.001"
                          min="0"
                          max="100"
                        />
                        <span className="input-group-text">kW / hr</span>
                      </div>
                      {errors.avg_parasitic_load && (
                        <span className="add-device-error-message">
                          {errors.avg_parasitic_load}
                        </span>
                      )}
                    </div>
                    <div className="add-device-form-group">
                      <label htmlFor="fractional_self_loss">
                        Fractional Self Loss:{" "}
                        <span className="required-asterisk">*</span>
                      </label>
                      <div className="input-group input-group-unified-focus">
                        <input
                          type="text"
                          className="form-control"
                          id="fractional_self_loss"
                          value={formData.fractional_self_loss}
                          onChange={handleChange}
                          placeholder="Enter Fractional Self Loss"
                          step="0.00001"
                          min="0"
                          max="1"
                        />
                        <span className="input-group-text">% / hr</span>
                      </div>
                      {errors.fractional_self_loss && (
                        <span className="add-device-error-message">
                          {errors.fractional_self_loss}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="add-device-form-group">
                    <label htmlFor="average_energy_used_during_operation">
                      Average Away Discharge: <span className="required-asterisk">*</span>
                    </label>
                    <div className="input-group input-group-unified-focus">
                      <input
                        type="text"
                        className="form-control"
                        id="average_energy_used_during_operation"
                        value={formData.average_energy_used_during_operation}
                        onChange={handleChange}
                        placeholder="Enter Average Away Discharge"
                        step="0.01"
                        min="0"
                        max="100"
                      />
                      <span className="input-group-text">%</span>
                    </div>
{errors.average_energy_used_during_operation && (
                        <span className="add-device-error-message">
                          {errors.average_energy_used_during_operation}
                      </span>
                    )}
                  </div>
                </>
              )}

              {/* BESS Storage Mode */}
              {formData.storage_mode === "BESS" && (

                <>
                  {/* Device Name */}
                  <div className="add-device-form-group">
                    <label htmlFor="device_name">
                      Device Name: <span className="required-asterisk">*</span>
                    </label>
                    <input
                      type="text"
                      maxLength={50}
                      id="device_name"
                      value={formData.device_name}
                      onChange={handleChange}
                      placeholder="Electrical Battery Storage "
                    />
                    {errors.device_name && (
                      <span className="add-device-error-message">
                        {errors.device_name}
                      </span>
                    )}
                  </div>

                  {/* Make and Model */}
                  <div className="add-device-form-group">
                    <label htmlFor="make_and_model">
                      Make and Model:
                    </label>
                    <input
                      type="text"
                      maxLength={50}
                      id="make_and_model"
                      value={formData.make_and_model}
                      onChange={handleChange}
                      placeholder="Tesla powerwall 2"
                    />
                    {errors.make_and_model && (
                      <span className="add-device-error-message">
                        {errors.make_and_model}
                      </span>
                    )}
                  </div>

                  {/* Serial Number */}
                  <div className="add-device-form-group">
                    <label htmlFor="serial_number">
                      Serial Number: 
                    </label>
                    <input
                      type="text"
                      id="serial_number"
                      maxLength={50}
                      value={formData.serial_number}
                      onChange={handleChange}
                      placeholder="SN8392"
                    />
                    {errors.serial_number && (
                      <span className="add-device-error-message">
                        {errors.serial_number}
                      </span>
                    )}
                  </div>

                  {/* Status */}
                  <div className="add-device-form-group">
                    <label htmlFor="status">
                      Status: <span className="required-asterisk">*</span>
                    </label>
                    <select id="status" value={formData.status} onChange={handleChange}>
                      <option value="" disabled>
                        -- Select Status --
                      </option>
                      <option value="connected">Connected</option>
                      <option value="disconnected">Disconnected</option>
                    </select>
                    {errors.status && (
                      <span className="add-device-error-message">{errors.status}</span>
                    )}
                  </div>

                  {/* Charging Strategy */}
                  <div className="add-device-form-group">
                    <label htmlFor="charging_strategy">
                      Charging Strategy{" "}
                      <span className="required-asterisk">*</span>
                    </label>
                    <select
                      id="charging_strategy"
                      value={formData.charging_strategy}
                      onChange={handleChange}
                    >
                      <option value="" disabled>
                        -- Select Charging Strategy --
                      </option>
                      <option value="unrestricted">Smart Charging</option>
                      <option value="generator_only">Generator Only</option>
                      <option value="generator_priority">Generator Priority</option>
                      <option value="grid_for_self_loads_only">Grid for Self Loads Only</option>
                      {/* <option value="unrestricted">Unrestricted</option> */}
                    </select>
                    {errors.charging_strategy && (
                      <span className="add-device-error-message">
                        {errors.charging_strategy}
                      </span>
                    )}
                  </div>

                  {/* BESS Discharging Strategy */}
                  <div className="add-device-form-group">
                    <label htmlFor="discharging_strategy">
                      Discharging Strategy{" "}
                      <span className="required-asterisk">*</span>
                    </label>
                    <select
                      id="discharging_strategy"
                      value={formData.discharging_strategy}
                      onChange={handleChange}
                    >
                      <option value="" disabled>
                        -- Select Discharging Strategy --
                      </option>
                      <option value="unrestricted">Unrestricted</option>
                      <option value="building_load_only">Building Load Only</option>
                      <option value="backup_only">Backup Only</option>
                      <option value="coupled_ev_only">Coupled EV Only</option>
                      {/* <option value="forbidden">Forbidden</option> */}
                    </select>
                    {errors.discharging_strategy && (
                      <span className="add-device-error-message">
                        {errors.discharging_strategy}
                      </span>
                    )}
                  </div>


                  <hr className="form-section-divider" />


                  {/* Max Energy and Daily Cycles */}
                  <div className="add-device-form-group-horizontal">

                    {/* Max Energy */}
                    <div className="add-device-form-group">
                      <label htmlFor="max_energy">
                        Max Energy: <span className="required-asterisk">*</span>
                      </label>
                      <div className="input-group input-group-unified-focus">
                        <input
                          type="text"
                          className="form-control"
                          id="max_energy"
                          value={formData.max_energy}
                          onChange={handleChange}
                          placeholder="Max Energy"
                          step="0.01"
                          min="0"
                        />
                        <span className="input-group-text">kWh</span>
                      </div>
                      {errors.max_energy && (
                        <span className="add-device-error-message">
                          {errors.max_energy}
                        </span>
                      )}
                    </div>

                    <div className="add-device-form-group" />

                  </div>
                  {/* Max Power Charging and Max Power Discharging */}
                  <div className="add-device-form-group-horizontal">

                    {/* Max Power Charging */}
                    <div className="add-device-form-group">
                      <label htmlFor="max_power_charging">
                        Max Power Charging: <span className="required-asterisk">*</span>
                      </label>
                      <div className="input-group input-group-unified-focus">
                        <input
                          type="text"
                          className="form-control"
                          id="max_power_charging"
                          value={formData.max_power_charging}
                          onChange={handleChange}
                          placeholder="Max Power Charging"
                          step="0.01"
                          min="0"
                        />
                        <span className="input-group-text">kW</span>
                      </div>
                      {errors.max_power_charging && (
                        <span className="add-device-error-message">
                          {errors.max_power_charging}
                        </span>
                      )}
                    </div>

                    {/* Max Power Discharging */}
                    <div className="add-device-form-group">
                      <label htmlFor="max_power_discharging">
                        Max Power Discharging:
                        {!isDischargeForbidden && (
                          <span className="required-asterisk"> *</span>
                        )}
                      </label>
                      <div className="input-group input-group-unified-focus">
                        <input
                          type="text"
                          className="form-control"
                          id="max_power_discharging"
                          value={formData.max_power_discharging}
                          onChange={handleChange}
                          placeholder="Max Power Discharging"
                          step="0.01"
                          min="0"
                          disabled={isDischargeForbidden}
                          style={
                            isDischargeForbidden
                              ? { backgroundColor: "#e9ecef", cursor: "not-allowed" }
                              : undefined
                          }
                        />
                        <span className="input-group-text">kW</span>
                      </div>
                      {errors.max_power_discharging && (
                        <span className="add-device-error-message">
                          {errors.max_power_discharging}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Charging and discharging efficiency */}
                  <div className="add-device-form-group-horizontal">

                    {/* Charging Efficiency */}
                    <div className="add-device-form-group">
                      <label htmlFor="charging_efficiency_fraction">
                        Charging Efficiency: <span className="required-asterisk">*</span>
                      </label>
                      <div className="input-group input-group-unified-focus">
                        <input
                          type="text"
                          className="form-control"
                          id="charging_efficiency_fraction"
                          value={formData.charging_efficiency_fraction}
                          onChange={handleChange}
                          placeholder="Charging Efficiency"
                          step="0.01"
                          min="0"
                        />
                        <span className="input-group-text">%</span>
                      </div>
                      {errors.charging_efficiency_fraction && (
                        <span className="add-device-error-message">
                          {errors.charging_efficiency_fraction}
                        </span>
                      )}
                    </div>

                    {/* Discharging Efficiency */}
                    <div className="add-device-form-group">
                      <label htmlFor="discharging_efficiency_fraction">
                        Discharging Efficiency:
                        {!isDischargeForbidden && (
                          <span className="required-asterisk"> *</span>
                        )}
                      </label>
                      <div className="input-group input-group-unified-focus">
                        <input
                          type="text"
                          className="form-control"
                          id="discharging_efficiency_fraction"
                          value={formData.discharging_efficiency_fraction}
                          onChange={handleChange}
                          placeholder="Discharging Efficiency"
                          step="0.01"
                          min="0"
                          max="100"
                          disabled={isDischargeForbidden}
                          style={
                            isDischargeForbidden
                              ? { backgroundColor: "#e9ecef", cursor: "not-allowed" }
                              : undefined
                          }
                        />
                        <span className="input-group-text">%</span>
                      </div>
                      {errors.discharging_efficiency_fraction && (
                        <span className="add-device-error-message">
                          {errors.discharging_efficiency_fraction}
                        </span>
                      )}
                    </div>
                  </div>









                  {/* Min SOC and Max SOC and Initial SOC */}
                  <div className="add-device-form-group-horizontal">

                    {/* Min SOC */}
                    <div className="add-device-form-group">
                      <label>
                        Minimum SOC: <span className="required-asterisk">*</span>
                      </label>
                      <div className="input-group input-group-unified-focus">
                        <input
                          type="text"
                          className="form-control"
                          id="min_soc"
                          placeholder="Min SoC"
                          min="0"
                          max="100"
                          onInvalid={(e) => e.preventDefault()}
                          step="1"
                          value={formData.min_soc}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              min_soc: e.target.value,
                            })
                          }
                        />
                        <span className="input-group-text">%</span>
                      </div>
                      {errors.min_soc && (
                        <span className="add-device-error-message">
                          {errors.min_soc}
                        </span>
                      )}
                      {minSocFloorPercent != null && (
                        <span
                          className={
                            "add-device-helper-text" +
                            (minSocViolatesFloor ? " is-violated" : "")
                          }
                        >
                          Min: {minSocFloorPercent.toFixed(1)}% (parasitic buffer)
                        </span>
                      )}
                    </div>

                    {/* Max SOC */}
                    <div className="add-device-form-group">
                      <label>
                        Maximum SOC: <span className="required-asterisk">*</span>
                      </label>
                      <div className="input-group input-group-unified-focus">
                        <input
                          type="text"
                          className="form-control"
                          id="max_soc"
                          placeholder="Max SoC"
                          min="1"
                          max="100"
                          step="1"
                          value={formData.max_soc ??""}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              max_soc: e.target.value,
                            })
                          }
                        />
                        <span className="input-group-text">%</span>
                      </div>
                      
                      {errors.max_soc && (
                        <span className="add-device-error-message">
                          {errors.max_soc}
                        </span>
                      )}
                    </div>

                    {/* Initial SOC */}
                    <div className="add-device-form-group">
                      <label>
                        Initial SOC: <span className="required-asterisk">*</span>
                      </label>
                      <div className="input-group input-group-unified-focus">
                        <input
                          type="text"
                          className="form-control"
                          id="initial_soc"
                          placeholder="Initial SoC"
                          min="0"
                          max="100"
                          step="1"
                          value={formData.initial_soc}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              initial_soc: e.target.value,
                            })
                          }
                        />
                        <span className="input-group-text">%</span>
                      </div>

                      {errors.initial_soc && (
                        <span className="add-device-error-message">
                          {errors.initial_soc}
                        </span>
                      )}
                    </div>
                  </div>

                  <hr className="form-section-divider" />

                  {/* Average Parasitic Load and Fractional Efficiency Loss */}
                  <div className="add-device-form-group-horizontal">
                    <div className="add-device-form-group">
                      <label htmlFor="avg_parasitic_load">
                        Average Parasitic Load:
                        <span className="required-asterisk">*</span>
                      </label>
                      <div className="input-group input-group-unified-focus">
                        <input
                          type="text"
                          className="form-control"
                          id="avg_parasitic_load"
                          value={formData.avg_parasitic_load}
                          onChange={handleChange}
                          placeholder="Enter Average Parasitic Load"
                          step="0.001"
                          min="0"
                          max="100"
                        />
                        <span className="input-group-text">kW / hr</span>
                      </div>
                      {errors.avg_parasitic_load && (
                        <span className="add-device-error-message">
                          {errors.avg_parasitic_load}
                        </span>
                      )}
                    </div>
                    <div className="add-device-form-group">
                      <label htmlFor="fractional_self_loss">
                        Fractional Self Loss:{" "}
                        <span className="required-asterisk">*</span>
                      </label>
                      <div className="input-group input-group-unified-focus">
                        <input
                          type="text"
                          className="form-control"
                          id="fractional_self_loss"
                          value={formData.fractional_self_loss}
                          onChange={handleChange}
                          placeholder="Enter Fractional Self Loss"
                          step="0.00001"
                          min="0"
                          max="1"
                        />
                        <span className="input-group-text">% / hr</span>
                      </div>
                      {errors.fractional_self_loss && (
                        <span className="add-device-error-message">
                          {errors.fractional_self_loss}
                        </span>
                      )}
                    </div>
                  </div>


                  
                  <hr className="form-section-divider" />


                </>
              )}

              {/* Coupled devices (EV or BESS): the picker and its dispatch note. */}
              {coupledDevicesField}

            </>
          )}



          {/* Production Device Fields */}
          {formData.device_type === "Production" && (
            <>
              {/* Production Device Type Selection - First after Device Type */}
              <div className="add-device-form-group">
                <label htmlFor="production_type">
                  Production Type: <span className="required-asterisk">*</span>
                </label>
                <select
                  id="production_type"
                  name="production_type"
                  value={formData.production_type}
                  disabled={!!device}
                  onChange={handleChange}
                >
                  <option value="" disabled>
                    -- Select Production Type --
                  </option>
                  <option value="Solar PV">Solar</option>
                  <option value="Fuel generator">Fueled Generator</option>
                </select>
                {errors.production_type && (
                  <span className="add-device-error-message">
                    {errors.production_type}
                  </span>
                )}
              </div>

              <hr className="form-section-divider" />

              {/* Common fields - shown when a production device type is selected */}
              {formData.production_type && (
                <>
                  {/* Device Name */}
                  <div className="add-device-form-group">
                    <label htmlFor="device_name">
                      Device Name: <span className="required-asterisk">*</span>
                    </label>
                    <input
                      type="text"
                      maxLength={50}
                      id="device_name"
                      value={formData.device_name}
                      onChange={handleChange}
                      placeholder={formData.production_type === "Solar PV" ? "Solar Panel Array" : "Backup Generator"}
                    />
                    {errors.device_name && (
                      <span className="add-device-error-message">
                        {errors.device_name}
                      </span>
                    )}
                  </div>

                  {/* Make and Model */}
                  <div className="add-device-form-group">
                    <label htmlFor="make_and_model">
                      Make and Model:
                    </label>
                    <input
                      type="text"
                      maxLength={50}
                      id="make_and_model"
                      value={formData.make_and_model}
                      onChange={handleChange}
                      placeholder={formData.production_type === "Solar PV" ? "SunPower Maxeon 6" : "Generac Guardian 24kW"}
                    />
                    {errors.make_and_model && (
                      <span className="add-device-error-message">
                        {errors.make_and_model}
                      </span>
                    )}
                  </div>

                  {/* Serial Number */}
                  <div className="add-device-form-group">
                    <label htmlFor="serial_number">
                      Serial Number:
                    </label>
                    <input
                      type="text"
                      id="serial_number"
                      maxLength={50}
                      value={formData.serial_number}
                      onChange={handleChange}
                      placeholder="SN8392"
                    />
                    {errors.serial_number && (
                      <span className="add-device-error-message">
                        {errors.serial_number}
                      </span>
                    )}
                  </div>

                  {/* Status */}
                  <div className="add-device-form-group">
                    <label htmlFor="status">
                      Status: <span className="required-asterisk">*</span>
                    </label>
                    <select id="status" value={formData.status} onChange={handleChange}>
                      <option value="" disabled>
                        -- Select Status --
                      </option>
                      <option value="connected">Connected</option>
                      <option value="disconnected">Disconnected</option>
                    </select>
                    {errors.status && (
                      <span className="add-device-error-message">{errors.status}</span>
                    )}
                  </div>

                  <hr className="form-section-divider" />

                  {/* Production Data File + derived power fields (Solar PV) */}
                  {formData.production_type === "Solar PV" && (
                    <>
                      <div className="add-device-form-group" style={{ marginBottom: 0 }}>
                        <label>
                          Production Data File (.csv) <span className="required">*</span>
                        </label>
                      </div>
                      <div className="csv-derived-group">
                        <div className="add-device-form-group">
                          <div className="file-input-container">
                          <div className="file-name-box">
                            <span
                              className={`file-name-text ${
                                formData.csv_file_name ? "file-selected" : "no-file"
                              }`}
                            >
                              {formData.csv_file_name || "No file selected"}
                            </span>
                            {uploading && <div className="upload-spinner" />}
                          </div>
                          <label htmlFor="csv-upload-production" className="add-device-upload-button">
                            Select File
                          </label>
                          <input
                            id="csv-upload-production"
                            type="file"
                            accept=".csv"
                            style={{ display: "none" }}
                            onChange={handleFileChange}
                          />
                        </div>

                        {errors.csv_file_name && (
                          <span className="add-device-error-message">
                            {errors.csv_file_name}
                          </span>
                        )}
                        {uploadMessage && (
                          <div
                            className={`upload-message ${
                              uploadMessage.type === "success" ? "success" : "error"
                            }`}
                          >
                            {uploadMessage.text}
                          </div>
                        )}
                        {renderUploadWarnings()}
                        {errors.uploadMessage && (
                          <span className="add-device-error-message">
                            {errors.uploadMessage}
                          </span>
                        )}
                      </div>

                      <div className="add-device-form-group-horizontal">
                        {/* Minimum Operating Power */}
                        <div className="add-device-form-group disabled-field-tooltip"
                          data-tooltip="Value is derived from the uploaded CSV file"
                        >
                          <label htmlFor="min_power_charging">
                            Minimum Operating Power: <span className="required-asterisk">*</span>
                          </label>
                          <div className="input-group input-group-unified-focus">
                            <input
                              type="text"
                              className="form-control"
                              id="min_power_charging"
                              value={formData.min_power_charging}
                              onChange={handleChange}
                              placeholder="Min Power"
                              step="0.01"
                              min="0"
                              disabled
                            />
                            <span className="input-group-text">kW</span>
                          </div>
                          {errors.min_power_charging && (
                            <span className="add-device-error-message">
                              {errors.min_power_charging}
                            </span>
                          )}
                        </div>
                        
                        {/* Maximum Operating Power */}
                        <div className="add-device-form-group disabled-field-tooltip"
                          data-tooltip="Value is derived from the uploaded CSV file"
                        >
                          <label htmlFor="max_power_charging">
                            Maximum Operating Power: <span className="required-asterisk">*</span>
                          </label>
                          <div className="input-group input-group-unified-focus">
                            <input
                              type="text"
                              className="form-control"
                              id="max_power_charging"
                              value={formData.max_power_charging}
                              onChange={handleChange}
                              placeholder="Max Power"
                              step="0.01"
                              min="0"
                              disabled
                            />
                            <span className="input-group-text">kW</span>
                          </div>
                          {errors.max_power_charging && (
                            <span className="add-device-error-message">
                              {errors.max_power_charging}
                            </span>
                          )}
                        </div>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Max and Min Operating Power (Fueled Generator - editable) */}
                  {formData.production_type !== "Solar PV" && formData.production_type && (
                    <div className="add-device-form-group-horizontal">
                      {/* Minimum Operating Power */}
                      <div className="add-device-form-group">
                        <label htmlFor="min_power_charging">
                          Minimum Operating Power: <span className="required-asterisk">*</span>
                        </label>
                        <div className="input-group input-group-unified-focus">
                          <input
                            type="text"
                            className="form-control"
                            id="min_power_charging"
                            value={formData.min_power_charging}
                            onChange={handleChange}
                            placeholder="Min Power"
                            step="0.01"
                            min="0"
                          />
                          <span className="input-group-text">kW</span>
                        </div>
                        {errors.min_power_charging && (
                          <span className="add-device-error-message">
                            {errors.min_power_charging}
                          </span>
                        )}
                      </div>
                      
                      {/* Maximum Operating Power */}
                      <div className="add-device-form-group">
                        <label htmlFor="max_power_charging">
                          Maximum Operating Power: <span className="required-asterisk">*</span>
                        </label>
                        <div className="input-group input-group-unified-focus">
                          <input
                            type="text"
                            className="form-control"
                            id="max_power_charging"
                            value={formData.max_power_charging}
                            onChange={handleChange}
                            placeholder="Max Power"
                            step="0.01"
                            min="0"
                          />
                          <span className="input-group-text">kW</span>
                        </div>
                        {errors.max_power_charging && (
                          <span className="add-device-error-message">
                            {errors.max_power_charging}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  <hr className="form-section-divider" />

                  {/* Solar PV Specific Fields */}
                  {formData.production_type === "Solar PV" && (
                    <>
                      {/* Yearly Degradation */}
                      <div className="add-device-form-group">
                        <label htmlFor="yearly_degradation">
                          Yearly Degradation: <span className="required-asterisk">*</span>
                        </label>
                        <div className="input-group input-group-unified-focus">
                          <input
                            type="text"
                            className="form-control"
                            id="yearly_degradation"
                            value={formData.yearly_degradation || ""}
                            onChange={handleChange}
                            placeholder="Enter Yearly Degradation"
                            step="0.001"
                            min="0.0"
                            max="0.01"
                          />
                          <span className="input-group-text">%</span>
                        </div>
                        {errors.yearly_degradation && (
                          <span className="add-device-error-message">
                            {errors.yearly_degradation}
                          </span>
                        )}
                      </div>
                    </>
                  )}

                  {/* Fueled Generator Specific Fields */}
                  {formData.production_type === "Fuel generator" && (
                    <>

                      {/* Startup/Shutdown Cost */}
                      <div className="add-device-form-group">
                        <label htmlFor="startup_cost">
                          Startup/Shutdown Cost: <span className="required-asterisk">*</span>
                        </label>
                        <div className="input-group input-group-unified-focus">
                          <input
                            type="text"
                            className="form-control"
                            id="startup_cost"
                            value={formData.startup_cost}
                            onChange={handleChange}
                            placeholder="Enter Startup/Shutdown cost"
                            step="0.01"
                            min="0"
                          />
                          <span className="input-group-text">$</span>
                        </div>
                        {errors.startup_cost && (
                          <span className="add-device-error-message">
                            {errors.startup_cost}
                          </span>
                        )}
                      </div>
                      {/* Fuel Type */}
                      <div className="add-device-form-group">
                        <label htmlFor="fuel_type">
                          Fuel Type: <span className="required-asterisk">*</span>
                        </label>
                        <select
                          id="fuel_type"
                          value={formData.fuel_type}
                          onChange={handleChange}
                        >
                          <option value="" disabled>
                            -- Select Fuel Type --
                          </option>
                          <option value="carbon">Carbon</option>
                          <option value="hydrogen">Hydrogen</option>
                        </select>
                        {errors.fuel_type && (
                          <span className="add-device-error-message">
                            {errors.fuel_type}
                          </span>
                        )}
                      </div>

                      {/* Fuel Storage Size and Consumption */}
                      <div className="add-device-form-group-horizontal">
                        <div className="add-device-form-group">
                          <label htmlFor="fuel_storage_size">
                            Fuel Storage Size: <span className="required-asterisk">*</span>
                          </label>
                          <div className="input-group input-group-unified-focus">
                            <input
                              type="text"
                              className="form-control"
                              id="fuel_storage_size"
                              value={formData.fuel_storage_size || ""}
                              onChange={handleChange}
                              placeholder="Enter fuel storage size"
                              step="0.01"
                              min="0"
                            />
                            <span className="input-group-text">kg</span>
                          </div>
                          {errors.fuel_storage_size && (
                            <span className="add-device-error-message">
                              {errors.fuel_storage_size}
                            </span>
                          )}
                        </div>

                        <div className="add-device-form-group">
                          <label htmlFor="consumption">
                            Consumption: <span className="required-asterisk">*</span>
                          </label>
                          <div className="input-group input-group-unified-focus">
                            <input
                              type="text"
                              className="form-control"
                              id="consumption"
                              value={formData.consumption || ""}
                              onChange={handleChange}
                              placeholder="Enter fuel consumption"
                              step="0.01"
                              min="0"
                            />
                            <span className="input-group-text">kg/kWh</span>
                          </div>
                          {errors.consumption && (
                            <span className="add-device-error-message">
                              {errors.consumption}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Carbon Emissions */}
                      <div className="add-device-form-group">
                        <label htmlFor="carbon_emissions">
                          Carbon Emissions: <span className="required-asterisk">*</span>
                        </label>
                        <div className="input-group input-group-unified-focus">
                          <input
                            type="text"
                            className="form-control"
                            id="carbon_emissions"
                            value={formData.carbon_emissions || ""}
                            onChange={handleChange}
                            placeholder="Enter carbon emissions"
                            step="0.01"
                            min="0"
                          />
                          <span className="input-group-text">g/kg</span>
                        </div>
                        {errors.carbon_emissions && (
                          <span className="add-device-error-message">
                            {errors.carbon_emissions}
                          </span>
                        )}
                      </div>

                      {/* Fuel Cost and Escalator */}
                      <div className="add-device-form-group-horizontal">
                        <div className="add-device-form-group">
                          <label htmlFor="fuel_cost_per_kg">
                            Fuel Cost per Kg: <span className="required-asterisk">*</span>
                          </label>
                          <div className="input-group input-group-unified-focus">
                            <input
                              type="text"
                              className="form-control"
                              id="fuel_cost_per_kg"
                              value={formData.fuel_cost_per_kg || ""}
                              onChange={handleChange}
                              placeholder="Enter fuel cost"
                              step="0.01"
                              min="0"
                            />
                            <span className="input-group-text">$/kg</span>
                          </div>
                          {errors.fuel_cost_per_kg && (
                            <span className="add-device-error-message">
                              {errors.fuel_cost_per_kg}
                            </span>
                          )}
                        </div>

                        <div className="add-device-form-group">
                          <label htmlFor="fuel_cost_escalator_fraction">
                            Fuel Cost Escalator: <span className="required-asterisk">*</span>
                          </label>
                          <div className="input-group input-group-unified-focus">
                            <input
                              type="text"
                              className="form-control"
                              id="fuel_cost_escalator_fraction"
                              value={formData.fuel_cost_escalator_fraction || ""}
                              onChange={handleChange}
                              placeholder="Enter escalator fraction"
                              step="0.01"
                              min="0"
                              max="0.15"
                            />
                            <span className="input-group-text">%</span>
                          </div>
                          {errors.fuel_cost_escalator_fraction && (
                            <span className="add-device-error-message">
                              {errors.fuel_cost_escalator_fraction}
                            </span>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}

              {/* Coupled devices (solar or generator): the picker and its dispatch note. */}
              {coupledDevicesField}
            </>
          )}

          
          {formData.device_type === "Consumption" && (
            <>
              {/* Device Name */}
              <div className="add-device-form-group">
                <label htmlFor="device_name">
                  Device Name: <span className="required-asterisk">*</span>
                </label>
                <input
                  type="text"
                  maxLength={50}
                  id="device_name"
                  value={formData.device_name}
                  onChange={handleChange}
                  placeholder="Building Load"
                />
                {errors.device_name && (
                  <span className="add-device-error-message">
                    {errors.device_name}
                  </span>
                )}
              </div>

              {/* Status */}
              <div className="add-device-form-group">
                <label htmlFor="status">
                  Status: <span className="required-asterisk">*</span>
                </label>
                <select id="status" value={formData.status} onChange={handleChange}>
                  <option value="" disabled>
                    -- Select Status --
                  </option>
                  <option value="connected">Connected</option>
                  <option value="disconnected">Disconnected</option>
                </select>
                {errors.status && (
                  <span className="add-device-error-message">{errors.status}</span>
                )}
              </div>

              {/* Building Load Data File + derived Max Power */}
              <div className="add-device-form-group" style={{ marginBottom: 0 }}>
                <label>
                  Building Load Data File (.csv) <span className="required">*</span>
                </label>
              </div>
              <div className="csv-derived-group">
                <div className="add-device-form-group">
                  <div className="file-input-container">
                    <div className="file-name-box">
                      <span
                        className={`file-name-text ${
                          formData.csv_file_name ? "file-selected" : "no-file"
                        }`}
                      >
                        {formData.csv_file_name || "No file selected"}
                      </span>
                      {uploading && <div className="upload-spinner" />}
                    </div>
                    <label htmlFor="csv-upload-consumption" className="add-device-upload-button">
                      Select File
                    </label>
                    <input
                      id="csv-upload-consumption"
                      type="file"
                      accept=".csv"
                      style={{ display: "none" }}
                      onChange={handleFileChange}
                    />
                  </div>
                  {errors.csv_file_name && (
                    <span className="add-device-error-message">
                      {errors.csv_file_name}
                    </span>
                  )}
                  {uploadMessage && (
                    <div
                      className={`upload-message ${
                        uploadMessage.type === "success" ? "success" : "error"
                      }`}
                    >
                      {uploadMessage.text}
                    </div>
                  )}
                  {renderUploadWarnings()}
                  {errors.uploadMessage && (
                    <span className="add-device-error-message">
                      {errors.uploadMessage}
                    </span>
                  )}
                </div>

                {/* Max Power (auto-filled from CSV) */}
                <div className="add-device-form-group disabled-field-tooltip"
                  data-tooltip="Value is derived from the uploaded CSV file"
                >
                  <label htmlFor="max_power_charging">
                    Max Power: <span className="required-asterisk">*</span>
                  </label>
                  <div className="input-group input-group-unified-focus">
                    <input
                      type="text"
                      className="form-control"
                      id="max_power_charging"
                      value={formData.max_power_charging}
                      onChange={handleChange}
                      placeholder="Max Power"
                      step="0.01"
                      min="0"
                      disabled
                    />
                    <span className="input-group-text">kW</span>
                  </div>
                  {errors.max_power_charging && (
                    <span className="add-device-error-message">
                      {errors.max_power_charging}
                    </span>
                  )}
                </div>
              </div>
            </>
          )}

          
          {formData.device_type === "Transfer" && (
            <div className="add-device-form-group">
              <label htmlFor="demand_Charge">
                Demand Charge ($/kW):{" "}
                <span className="required-asterisk">*</span>
              </label>
              <input
                type="number"
                id="demand_charge"
                value={formData.demand_charge}
                onChange={handleChange}
                placeholder="Enter Demand Charge"
                step="0.01"
                min="0"
                max="1"
              />
              {errors.demand_charge && (
                <span className="add-device-error-message">
                  {errors.demand_charge}
                </span>
              )}
              <div className="form-section" style={{ marginTop: "16px" }}>
                <label htmlFor="demand_Charge_Escalato_Fraction">
                  Demand Charge Escalator Fraction ($/kW):{" "}
                  <span className="required-asterisk">*</span>
                </label>
                <input
                  type="number"
                  id="demand_charge_escalator_fraction"
                  value={formData.demand_charge_escalator_fraction}
                  onChange={handleChange}
                  placeholder="Enter Demand Charge Escalator Fraction"
                  step="0.01"
                  min="0"
                  max="1"
                />
                {errors.demand_charge_escalator_fraction && (
                  <span className="add-device-error-message">
                    {errors.demand_charge_escalator_fraction}
                  </span>
                )}

                <div className="form-section" style={{ marginTop: "16px" }}>
                  <label>
                    Utility Tariff Data File (.csv){" "}
                    <span style={{ color: "red" }}>*</span>
                  </label>
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      alignItems: "center",
                      marginTop: "4px",
                    }}
                  >
                    {/* File name box with spinner */}
                    <div
                      style={{
                        flex: 1,
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        padding: "6px 8px",
                        backgroundColor: "#f9f9f9",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        minHeight: "36px",
                      }}
                    >
                      <span
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {formData.csv_file_name || "No file selected"}
                      </span>
                      {uploading && (
                        <div
                          className="upload-spinner"
                          style={{ marginLeft: "10px" }}
                        />
                      )}
                    </div>

                    {/* File input button */}
                    <label
                      htmlFor="csv-upload"
                      style={{
                        backgroundColor: "#29821b",
                        color: "white",
                        padding: "6px 12px",
                        borderRadius: "4px",
                        cursor: "pointer",
                      }}
                    >
                      Select File
                    </label>

                    <input
                      id="csv-upload"
                      type="file"
                      accept=".csv"
                      style={{ display: "none" }}
                      onChange={handleFileChange}
                    />
                  </div>

                  {errors.csv_file_name && (
                    <span className="add-device-error-message">
                      {errors.csv_file_name}
                    </span>
                  )}
                  {uploadMessage && (
                    <div
                      style={{
                        marginTop: "8px",
                        padding: "8px 12px",
                        borderRadius: "4px",
                        backgroundColor:
                          uploadMessage.type === "success"
                            ? "#e6f4ea"
                            : "#fbeaea",
                        color:
                          uploadMessage.type === "success"
                            ? "#276749"
                            : "#9b2c2c",
                        border: `1px solid ${uploadMessage.type === "success" ? "#c6f6d5" : "#feb2b2"}`,
                      }}
                    >
                      {uploadMessage.text}
                    </div>
                  )}
                  {renderUploadWarnings()}
                </div>
              </div>
            </div>
          )}

          <div className="add-device-form-actions">
            <button className="add-device-cancell-button" onClick={onClose}>
              Cancel
            </button>
            <button
              className="add-device-save-button"
              style={{ marginLeft: "10px" }}
              disabled={isSaving}
              onClick={() => {
                handleSave();
              }}
            >
              {isSaving ? "Saving..." : "Save"}
            </button>

           
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddDevicePopup;
